import React, { useState } from 'react';
import { CartItem } from '../types';
import { initiateCheckout } from '../utils/stripe';
import styles from './Cart.module.css';

interface CartProps {
  items: CartItem[];
  onUpdateQuantity: (itemId: string, newQuantity: number) => void;
  onRemoveItem: (itemId: string) => void;
  onCheckout?: () => void;
}

const Cart: React.FC<CartProps> = ({
  items,
  onUpdateQuantity,
  onRemoveItem,
  onCheckout,
}) => {
  const [email, setEmail] = useState('');
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [shippingOption, setShippingOption] = useState<
    'local' | 'standard' | 'express'
  >('local');
  const [shippingAddress, setShippingAddress] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
  });

  const total = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isAddressComplete = () => {
    if (shippingOption === 'local') return true;
    return Object.values(shippingAddress).every(value => value.trim() !== '');
  };

  const handleCheckout = async () => {
    // Validate email
    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    // Validate shipping address if shipping is selected
    if (shippingOption !== 'local' && !isAddressComplete()) {
      alert('Please fill in all shipping address fields');
      return;
    }

    setEmailError('');
    setIsCheckingOut(true);

    try {
      // Store email and shipping info in localStorage for the checkout process
      window.localStorage.setItem('checkoutEmail', email);
      window.localStorage.setItem('shippingOption', shippingOption);
      if (shippingOption !== 'local') {
        window.localStorage.setItem(
          'shippingAddress',
          JSON.stringify(shippingAddress)
        );
      }

      await initiateCheckout();
      if (onCheckout) onCheckout();
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Checkout failed. Please try again.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  return (
    <div className={styles.cart}>
      <h2 className={styles.cartTitle}>Shopping Cart</h2>
      {items.length === 0 ? (
        <p className={styles.cartEmpty}>Your cart is empty</p>
      ) : (
        <>
          {items.map(item => (
            <div key={item.id} className={styles.cartItem}>
              <img
                src={item.imageUrl}
                alt={item.name}
                className={styles.cartItemImage}
              />
              <div className={styles.cartItemContent}>
                <h3 className={styles.cartItemName}>{item.name}</h3>
                <p className={styles.cartItemPrice}>${item.price.toFixed(2)}</p>
                {item.size && (
                  <p className={styles.cartItemSize}>Size: {item.size}</p>
                )}
                <div className={styles.quantityControls}>
                  <button
                    onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                    className={styles.quantityButton}
                  >
                    -
                  </button>
                  <span className={styles.quantityDisplay}>
                    {item.quantity}
                  </span>
                  <button
                    onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                    className={styles.quantityButton}
                  >
                    +
                  </button>
                </div>
                <button
                  onClick={() => onRemoveItem(item.id)}
                  className={styles.removeButton}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
          <div className={styles.cartSummary}>
            {/* Shipping Options */}
            <div className={styles.shippingSection}>
              <h3 className={styles.shippingTitle}>Shipping Options</h3>
              <div className={styles.shippingOptions}>
                <label className={styles.shippingOption}>
                  <input
                    type="radio"
                    name="shipping"
                    value="local"
                    checked={shippingOption === 'local'}
                    onChange={e =>
                      setShippingOption(
                        e.target.value as 'local' | 'standard' | 'express'
                      )
                    }
                  />
                  <span className={styles.shippingLabel}>
                    Local Pickup | Free
                    <span className={styles.shippingDetails}>
                      Available at our store location
                    </span>
                  </span>
                </label>
                <label className={styles.shippingOption}>
                  <input
                    type="radio"
                    name="shipping"
                    value="standard"
                    checked={shippingOption === 'standard'}
                    onChange={e =>
                      setShippingOption(
                        e.target.value as 'local' | 'standard' | 'express'
                      )
                    }
                  />
                  <span className={styles.shippingLabel}>
                    Standard Shipping
                    <span className={styles.shippingDetails}>
                      5-7 business days
                    </span>
                  </span>
                </label>
                <label className={styles.shippingOption}>
                  <input
                    type="radio"
                    name="shipping"
                    value="express"
                    checked={shippingOption === 'express'}
                    onChange={e =>
                      setShippingOption(
                        e.target.value as 'local' | 'standard' | 'express'
                      )
                    }
                  />
                  <span className={styles.shippingLabel}>
                    Express Shipping
                    <span className={styles.shippingDetails}>
                      2-3 business days
                    </span>
                  </span>
                </label>
              </div>
            </div>

            {/* Shipping Address Form - Only show when shipping is selected */}
            {shippingOption !== 'local' && (
              <div className={styles.shippingAddressForm}>
                <h3 className={styles.addressFormTitle}>Shipping Address</h3>
                <div className={styles.addressFields}>
                  <div className={styles.addressRow}>
                    <input
                      type="text"
                      placeholder="First Name"
                      value={shippingAddress.firstName}
                      onChange={e =>
                        setShippingAddress(prev => ({
                          ...prev,
                          firstName: e.target.value,
                        }))
                      }
                      className={styles.addressInput}
                      required
                    />
                    <input
                      type="text"
                      placeholder="Last Name"
                      value={shippingAddress.lastName}
                      onChange={e =>
                        setShippingAddress(prev => ({
                          ...prev,
                          lastName: e.target.value,
                        }))
                      }
                      className={styles.addressInput}
                      required
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Street Address"
                    value={shippingAddress.address}
                    onChange={e =>
                      setShippingAddress(prev => ({
                        ...prev,
                        address: e.target.value,
                      }))
                    }
                    className={styles.addressInput}
                    required
                  />
                  <div className={styles.addressRow}>
                    <input
                      type="text"
                      placeholder="City"
                      value={shippingAddress.city}
                      onChange={e =>
                        setShippingAddress(prev => ({
                          ...prev,
                          city: e.target.value,
                        }))
                      }
                      className={styles.addressInput}
                      required
                    />
                    <select
                      value={shippingAddress.state}
                      onChange={e =>
                        setShippingAddress(prev => ({
                          ...prev,
                          state: e.target.value,
                        }))
                      }
                      className={styles.addressSelect}
                      required
                    >
                      <option value="">Select State</option>
                      <option value="AL">Alabama</option>
                      <option value="AK">Alaska</option>
                      <option value="AZ">Arizona</option>
                      <option value="AR">Arkansas</option>
                      <option value="CA">California</option>
                      <option value="CO">Colorado</option>
                      <option value="CT">Connecticut</option>
                      <option value="DE">Delaware</option>
                      <option value="FL">Florida</option>
                      <option value="GA">Georgia</option>
                      <option value="HI">Hawaii</option>
                      <option value="ID">Idaho</option>
                      <option value="IL">Illinois</option>
                      <option value="IN">Indiana</option>
                      <option value="IA">Iowa</option>
                      <option value="KS">Kansas</option>
                      <option value="KY">Kentucky</option>
                      <option value="LA">Louisiana</option>
                      <option value="ME">Maine</option>
                      <option value="MD">Maryland</option>
                      <option value="MA">Massachusetts</option>
                      <option value="MI">Michigan</option>
                      <option value="MN">Minnesota</option>
                      <option value="MS">Mississippi</option>
                      <option value="MO">Missouri</option>
                      <option value="MT">Montana</option>
                      <option value="NE">Nebraska</option>
                      <option value="NV">Nevada</option>
                      <option value="NH">New Hampshire</option>
                      <option value="NJ">New Jersey</option>
                      <option value="NM">New Mexico</option>
                      <option value="NY">New York</option>
                      <option value="NC">North Carolina</option>
                      <option value="ND">North Dakota</option>
                      <option value="OH">Ohio</option>
                      <option value="OK">Oklahoma</option>
                      <option value="OR">Oregon</option>
                      <option value="PA">Pennsylvania</option>
                      <option value="RI">Rhode Island</option>
                      <option value="SC">South Carolina</option>
                      <option value="SD">South Dakota</option>
                      <option value="TN">Tennessee</option>
                      <option value="TX">Texas</option>
                      <option value="UT">Utah</option>
                      <option value="VT">Vermont</option>
                      <option value="VA">Virginia</option>
                      <option value="WA">Washington</option>
                      <option value="WV">West Virginia</option>
                      <option value="WI">Wisconsin</option>
                      <option value="WY">Wyoming</option>
                    </select>
                    <input
                      type="text"
                      placeholder="ZIP Code"
                      value={shippingAddress.zipCode}
                      onChange={e =>
                        setShippingAddress(prev => ({
                          ...prev,
                          zipCode: e.target.value,
                        }))
                      }
                      className={styles.addressInput}
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Simple Total Section */}
            <div className={styles.cartTotalSection}>
              <h3 className={styles.cartTotalTitle}>Order Summary</h3>
              <div className={styles.totalBreakdown}>
                <div className={styles.totalRow}>
                  <span>Subtotal:</span>
                  <span>${total.toFixed(2)}</span>
                </div>
                <div className={styles.totalRow}>
                  <span>Shipping:</span>
                  <span>
                    {shippingOption === 'local'
                      ? 'Free'
                      : 'Calculated at checkout'}
                  </span>
                </div>
                <div className={styles.totalRow}>
                  <span>Tax:</span>
                  <span>Calculated at checkout</span>
                </div>
                <div className={styles.totalRowTotal}>
                  <span>Total:</span>
                  <span>Calculated at checkout</span>
                </div>
              </div>
              <p className={styles.checkoutNote}>
                * Final shipping cost, tax, and total will be calculated during
                checkout
              </p>
            </div>

            {/* Email Collection */}
            <div className={styles.emailSection}>
              <label htmlFor="checkout-email" className={styles.emailLabel}>
                Email Address *
              </label>
              <input
                id="checkout-email"
                type="email"
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError('');
                }}
                placeholder="Enter your email address"
                className={`${styles.emailInput} ${
                  emailError ? styles.error : ''
                }`}
                required
              />
              {emailError && <p className={styles.emailError}>{emailError}</p>}
              <p className={styles.emailHelp}>
                We&apos;ll send your order confirmation to this email address.
              </p>
            </div>

            <button
              onClick={handleCheckout}
              disabled={isCheckingOut || !email.trim() || !isAddressComplete()}
              className={styles.checkoutButton}
            >
              {isCheckingOut ? 'Processing...' : 'Proceed to Checkout'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Cart;
